import React, { FC } from 'react';
import { Dimensions, StyleSheet } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useIsFocused } from '@react-navigation/native';
import { useCurveLinesContext } from '@v2/pages/common/components/NCFileBatchEvents/context/CurveLinesContext';
import { Position, TimeCategory } from '@interfaces/base';
import { TILE_COLORS } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/constants';
import { ColorFunction } from '@styles/colors/types';

interface ConnectorLinesProps {
  C: ColorFunction;
  markerYOffset?: number;
}

const CurveLines: FC<ConnectorLinesProps> = ({ C, markerYOffset = 0 }) => {
  const { positions, isResizing, hasSettledOnce } = useCurveLinesContext();
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
  const { containerOffset } = positions;
  const isFocused = useIsFocused();

  // Don't render if tab is not focused (prevents showing stale lines during tab switch)
  if (!isFocused) {
    return null;
  }

  // Wait for the enclosing bottom-sheet (if any) to finish its open animation at least once,
  // so measurements aren't taken from a mid-animation layout.
  if (!hasSettledOnce) {
    return null;
  }

  // Don't render until we have the container offset
  if (!containerOffset) {
    return null;
  }

  if (isResizing) {
    return null;
  }

  const svgHeight = screenHeight - containerOffset.y;
  const svgWidth = screenWidth - containerOffset.x;

  // Guard against invalid dimensions (can happen during animations)
  if (svgHeight <= 0 || svgWidth <= 0) {
    return null;
  }

  const createCurvePath = (tileDot: Position, marker: Position): string => {
    // Convert window coordinates to container-relative coordinates
    const startX = tileDot.x - containerOffset.x;
    const startY = tileDot.y - containerOffset.y;
    const markerX = marker.x - containerOffset.x;
    const markerY = marker.y - containerOffset.y - markerYOffset;
    // Extend to bottom of SVG
    const endY = svgHeight;

    // Calculate the vertical distance available for the curve
    const verticalDistance = markerY - startY;

    // Create a smooth curve that gradually transitions from vertical to horizontal
    // First control point: starts curving toward marker early for smooth departure
    const ctrl1X = startX + (markerX - startX) * 0.4;
    const ctrl1Y = startY + verticalDistance * 0.75;
    // Second control point: above marker for smooth vertical entry
    const ctrl2X = markerX;
    const ctrl2Y = startY + verticalDistance * 0.25;

    // Path: smooth curve from tileDot to marker, then straight line down
    return `M ${startX} ${startY} C ${ctrl1X} ${ctrl1Y}, ${ctrl2X} ${ctrl2Y}, ${markerX} ${markerY} L ${markerX} ${endY}`;
  };

  return (
    <Svg width={svgWidth} height={svgHeight} style={[StyleSheet.absoluteFill, { zIndex: 10000000000 }]} pointerEvents="box-none">
      {Object.values(TimeCategory).map((type) => {
        const tileDot = positions.tileDots[type];
        const marker = positions.markerPositions[type];
        if (!tileDot || !marker) {
          return null;
        }

        return (
          <Path
            key={type}
            d={createCurvePath(tileDot, marker)}
            stroke={C(TILE_COLORS[type])}
            strokeWidth={StyleSheet.hairlineWidth}
            fill="none"
            pointerEvents="none"
          />
        );
      })}
    </Svg>
  );
};

export default CurveLines;
